import React, { useState } from 'react';
import { Button, Drawer, Menu, Popover, useToasts } from 'freemium-ui';
import { History } from 'history';
import { isNil } from 'lodash';
import qs from 'query-string';
import { useTranslation } from 'react-i18next';
import { useHistory, useLocation } from 'react-router-dom';
import { useGetContactListsCount } from 'routes/screens/lists/hooks';
import { useGetSegmentsCount } from 'routes/screens/segments/hooks';
import {
  AccountInfoType,
  ConfirmationType,
  FormServFieldType,
} from '../../../shared/types';
import Icon from '../../../shared/components/Icon';
import { LastSeparateMenu } from '../../../shared/styles';
import { useAccessControl } from '../../../utils/access-control';
import {
  CONTACTS_IMPORT_LIMIT,
  CONTACT_LISTS_LIMIT,
  SEGMENTS_LIMIT,
  defaultOptions,
} from '../../constants';
import ImportContacts from './ImportContacts';
import NewContactForm from './NewContactForm';
import NewListAndImportForm from './NewListAndImportForm';

type NewButtonProps = {
  executeSegment?: () => void;
  disabled?: boolean;
  fields: FormServFieldType[];
  setConfirmationData: (
    str: ConfirmationType | React.SetStateAction<ConfirmationType>,
  ) => void;
  accountInfo?: AccountInfoType;
  contactsCount: number;
};

const NewButton = (props: NewButtonProps) => {
  const { t } = useTranslation();
  const {
    fields,
    executeSegment,
    setConfirmationData,
    disabled = false,
    accountInfo,
    contactsCount,
  } = props;
  const {
    push,
    location: { pathname },
  }: History = useHistory();
  const [isFreddyPage, setIsFreddyPage] = useState(false);
  const [, setToast] = useToasts();
  const routerLocation = useLocation();
  const [anchorEl, setAnchorEl] = React.useState<any>(null);
  const [isDirty, setIsDirty] = React.useState<boolean>(false);
  const [isNewContactsDrawerOpen, toggleNewContactsDrawer] =
    React.useState<boolean>(false);
  const [isImportContactsDrawerOpen, toggleImportContactsDrawer] =
    React.useState<boolean>(false);
  const [isNewListDialogOpen, toggleListDialog] =
    React.useState<boolean>(false);
  const { access = [] } = useAccessControl();

  setTimeout(() => {
    setIsFreddyPage(
      pathname.includes('/survey/create') &&
        document.querySelector('mfe-application[app-id="ask-freddy"]') !== null,
    );
  }, 500);

  const { data: contactListsCountData, isLoading: isLoadingContactListsCount } =
    useGetContactListsCount({
      enabled: access.includes('contact:write'),
    });
  const { data: segmentsCountData, isLoading: isLoadingSegmentsCount } =
    useGetSegmentsCount({
      enabled: access.includes('contact:write'),
    });

  React.useEffect(() => {
    if (
      !routerLocation ||
      !routerLocation.search ||
      access.length === 0 ||
      isNil(contactListsCountData) ||
      isNil(segmentsCountData)
    )
      return;
    const parsedValues = qs.parse(routerLocation.search);
    const {
      open,
      ...rest
    }: { open?: 'list' | 'segment' | 'contact' | 'import' } = parsedValues;
    if (!open) return;
    if (!access.includes('contact:write')) return;
    if (open === 'list') {
      if (
        (contactListsCountData || 0) >=
        (Number(accountInfo?.resource_limit_configuration?.contact_lists) ||
          CONTACT_LISTS_LIMIT)
      )
        setToast({
          type: 'warning',
          delay: 3000,
          text: `${t('forms.toast.warning.contact_lists_limit_reached')}`,
        });
      else toggleListDialog(true);
    }
    if (open === 'segment') {
      if (
        (Number(`${segmentsCountData?.count}`) || 0) >=
        (Number(accountInfo?.resource_limit_configuration?.segments) ||
          SEGMENTS_LIMIT)
      )
        setToast({
          type: 'warning',
          delay: 3000,
          text: `${t('forms.toast.warning.segment_limit_reached')}`,
        });
      else {
        window.open('/app/segments/create', '_blank');
      }
    }
    if (open === 'contact') toggleNewContactsDrawer(true);
    if (open === 'import') toggleImportContactsDrawer(true);
    if (open || Object.keys(rest).length > 0) {
      const newURL = qs.stringifyUrl({
        url: routerLocation.pathname,
        query: { ...(rest || {}) },
      });
      push(newURL);
    }
  }, [routerLocation.search, access, contactListsCountData, segmentsCountData]);

  const accessControlledOptions = React.useMemo(() => {
    if (!access) return [];
    return defaultOptions
      .filter((opt) => {
        switch (opt.id) {
          case 'survey':
          case 'import_survey':
            return access.includes('survey:write');
          case 'contact':
          case 'list':
          case 'segment':
            return access.includes('contact:write');
          case 'import':
            return (
              access.includes('contact:write') &&
              access.includes('contact-impexp:write')
            );
          default:
            return false;
        }
      })
      .map((opt) => {
        switch (opt.id) {
          case 'list':
            return {
              ...opt,
              disabled:
                isLoadingContactListsCount ||
                (contactListsCountData || 0) >=
                  (Number(
                    accountInfo?.resource_limit_configuration?.contact_lists,
                  ) || CONTACT_LISTS_LIMIT),
            };
          case 'segment':
            return {
              ...opt,
              disabled:
                isLoadingSegmentsCount ||
                (Number(`${segmentsCountData?.count}`) || 0) >=
                  (Number(
                    accountInfo?.resource_limit_configuration?.segments,
                  ) || SEGMENTS_LIMIT),
            };
          case 'contact':
          case 'import':
            return {
              ...opt,
              disabled:
                contactsCount >=
                (Number(accountInfo?.resource_limit_configuration?.contacts) ||
                  CONTACTS_IMPORT_LIMIT),
            };
          case 'survey':
          case 'import_survey':
            return {
              ...opt,
              disabled: !isFreddyPage && pathname.includes('/survey/create'),
            };
          default:
            return opt;
        }
      });
  }, [
    contactsCount,
    access,
    isLoadingContactListsCount,
    contactListsCountData,
    accountInfo?.resource_limit_configuration?.contact_lists,
    accountInfo?.resource_limit_configuration?.segments,
    isLoadingSegmentsCount,
    segmentsCountData?.count,
    isFreddyPage,
    pathname,
  ]);

  return (
    <>
      {accessControlledOptions.length > 0 && (
        <Button
          type="secondary"
          inline
          disabled={disabled}
          onClick={(e) => setAnchorEl(e.currentTarget)}
          aria-describedby="new-button-header"
        >
          + {t('new')} &nbsp;
          <Icon glyph="arrow-down" width={8} height={8} fill="#12344D" />
        </Button>
      )}
      <Popover
        id="new-button-header"
        anchorEl={anchorEl}
        onClose={() => setAnchorEl(null)}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'right',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
      >
        <LastSeparateMenu seperator seperatorPositionFromLast={2}>
          <Menu
            options={accessControlledOptions?.map((option) => ({
              ...option,
              name: `${t(option.name)}`,
            }))}
            onSelect={({ id }) => {
              switch (id) {
                case 'contact':
                  toggleNewContactsDrawer(true);
                  break;
                case 'list':
                  toggleListDialog(true);
                  break;
                case 'import':
                  toggleImportContactsDrawer(true);
                  break;
                case 'segment':
                  push('/segments/create');
                  break;
                case 'survey':
                  push('/survey/create', { isFreddyPage });
                  break;
                case 'import_survey':
                  push('/survey/create?import=true', { isFreddyPage });
                  break;
                default:
              }
            }}
            closeMenuCallback={() => setAnchorEl(null)}
          />
        </LastSeparateMenu>
      </Popover>
      <Drawer
        isOpen={isNewContactsDrawerOpen}
        onDismiss={() => {
          if (isDirty) {
            setConfirmationData({
              id: new Date().toISOString(),
              title: `${t('forms.drawer.contact_title')}`,
              onConfirm: () => {
                toggleNewContactsDrawer(false);
                setConfirmationData({});
              },
              description: `${t('forms.drawer.contact_desc')}`,
            });
          } else {
            toggleNewContactsDrawer(false);
          }
        }}
        isFullHeight
        clickOutToClose={false}
        size="large"
        placement="right"
      >
        <NewContactForm
          setIsDirty={(d: boolean) => setIsDirty(d)}
          fields={fields}
          executeSegment={executeSegment}
          toggleDrawer={() => toggleNewContactsDrawer(false)}
        />
      </Drawer>
      <Drawer
        isOpen={isImportContactsDrawerOpen}
        onDismiss={() => toggleImportContactsDrawer(false)}
        isFullHeight
        size="medium"
        clickOutToClose={false}
        placement="right"
      >
        <ImportContacts
          fullHeight
          fields={fields}
          executeSegment={executeSegment}
          toggleDrawer={() => toggleImportContactsDrawer(false)}
          accountInfo={accountInfo}
          contactsCount={contactsCount}
        />
      </Drawer>
      <Drawer
        isOpen={isNewListDialogOpen}
        isFullHeight
        clickOutToClose={false}
        placement="right"
        onDismiss={() => {
          if (isDirty) {
            setConfirmationData({
              id: new Date().toISOString(),
              title: `${t('forms.drawer.contact_title')}`,
              onConfirm: () => {
                toggleListDialog(false);
                setConfirmationData({});
              },
              description: `${t('forms.drawer.contact_desc')}`,
            });
          } else {
            toggleListDialog(false);
          }
        }}
      >
        <NewListAndImportForm
          fields={fields}
          setIsDirty={(d: boolean) => setIsDirty(d)}
          toggleDrawer={() => toggleListDialog(false)}
          accountInfo={accountInfo}
          contactsCount={contactsCount}
        />
      </Drawer>
    </>
  );
};

export default NewButton;
